import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { useProjectId } from "@/hooks/use-project-id";
import { Clock, BookOpen, Award, TrendingUp, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function MobileTutorProgress() {
  const { userId } = useRole();
  const { data: projectId } = useProjectId();
  const navigate = useNavigate();

  const { data: userLocation } = useQuery({
    queryKey: ["mobile-tutor-location", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data } = await supabase
        .from("user_locations")
        .select("location_id, locations(name)")
        .eq("user_id", userId)
        .maybeSingle();
      return data;
    },
    enabled: !!userId,
  });

  const { data: metrics } = useQuery({
    queryKey: ["mobile-tutor-metrics", projectId, userId],
    queryFn: async () => {
      if (!projectId || !userId) return null;
      const [
        { count: total },
        { count: completed },
        { data: certs },
      ] = await Promise.all([
        supabase.from("course_enrollments").select("*", { count: "exact", head: true }).eq("project_id", projectId).eq("user_id", userId),
        supabase.from("course_enrollments").select("*", { count: "exact", head: true }).eq("project_id", projectId).eq("user_id", userId).eq("completed", true),
        supabase.from("certificates").select("id", { count: "exact", head: true }).eq("project_id", projectId).eq("user_id", userId),
      ]);
      const t = total || 0;
      const c = completed || 0;
      return {
        total: t,
        completed: c,
        progress: t > 0 ? Math.round((c / t) * 100) : 0,
        certificates: certs?.length || 0,
      };
    },
    enabled: !!projectId && !!userId,
    staleTime: 2 * 60_000,
  });

  const { data: courses = [] } = useQuery({
    queryKey: ["mobile-tutor-courses", projectId, userId],
    queryFn: async () => {
      if (!projectId || !userId) return [];
      const { data } = await supabase
        .from("course_enrollments")
        .select("course_id, progress, completed, courses!inner(title)")
        .eq("project_id", projectId)
        .eq("user_id", userId)
        .eq("courses.status", "active")
        .eq("courses.archived", false);
      return (data || []).map((e: any) => ({
        title: e.courses?.title || "Unknown",
        rate: e.progress ?? (e.completed ? 100 : 0),
      }));
    },
    enabled: !!projectId && !!userId,
    staleTime: 5 * 60_000,
  });

  const m = metrics || { total: 0, completed: 0, progress: 0, certificates: 0 };
  const notStarted = courses.filter((c) => c.rate === 0);
  const inProgress = courses.filter((c) => c.rate > 0 && c.rate < 100);
  const locationName = userLocation?.locations
    ? (userLocation.locations as { name: string }).name
    : null;

  return (
    <div className="space-y-5 pt-2">
      {/* Header */}
      <div>
        <h1 className="text-[22px] font-bold" style={{ color: "#1B2D5B" }}>Learning Dashboard</h1>
        <p className="text-[13px]" style={{ color: "#9a8a7a" }}>
          {locationName ? `${locationName} · ` : ""}
          {new Date().toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
        </p>
      </div>

      {/* Stat Grid 2x2 */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="My Progress" value={`${m.progress}%`} sub={`${m.completed} of ${m.total} courses`} icon={<TrendingUp className="h-4 w-4" />} />
        <StatCard label="Assigned" value={String(m.total)} sub="enrolled courses" icon={<BookOpen className="h-4 w-4" />} valueColor="#F44321" />
        <StatCard label="Completed" value={String(m.completed)} sub="courses finished" icon={<Award className="h-4 w-4" />} />
        <StatCard label="Certificates" value={String(m.certificates)} sub="earned so far" icon={<Award className="h-4 w-4" />} />
      </div>

      {/* Not started */}
      <CourseSection
        label={`Not yet started · ${notStarted.length}`}
        courses={notStarted}
        emptyText="All courses have been started"
        emptyIcon={<BookOpen className="h-6 w-6" style={{ color: "#9a8a7a" }} />}
        badgeType="not_started"
        onTap={() => navigate("/learning/my-progress")}
      />

      {/* In progress */}
      <CourseSection
        label={`In progress · ${inProgress.length}`}
        courses={inProgress}
        emptyText="No courses in progress"
        emptyIcon={<BookOpen className="h-6 w-6" style={{ color: "#9a8a7a" }} />}
        badgeType="in_progress"
        onTap={() => navigate("/learning/my-progress")}
      />
    </div>
  );
}

function StatCard({ label, value, sub, icon, valueColor }: { label: string; value: string; sub: string; icon: React.ReactNode; valueColor?: string }) {
  return (
    <div className="rounded-[14px] p-4" style={{ background: "#ffffff", border: "0.5px solid #e8e0d8" }}>
      <div className="flex items-center gap-1.5 mb-2" style={{ color: "#9a8a7a" }}>
        {icon}
        <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-extrabold" style={{ color: valueColor || "#1B2D5B" }}>{value}</p>
      <p className="text-[11px] mt-0.5" style={{ color: "#9a8a7a" }}>{sub}</p>
    </div>
  );
}

function CourseSection({ label, courses, emptyText, emptyIcon, badgeType, onTap }: {
  label: string;
  courses: { title: string; rate: number }[];
  emptyText: string;
  emptyIcon: React.ReactNode;
  badgeType: "not_started" | "in_progress";
  onTap: () => void;
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#9a8a7a" }}>{label}</p>
      {courses.length === 0 ? (
        <div className="rounded-[14px] py-8 flex flex-col items-center justify-center" style={{ background: "#ffffff", border: "0.5px solid #e8e0d8" }}>
          {emptyIcon}
          <p className="text-[13px] mt-2" style={{ color: "#9a8a7a" }}>{emptyText}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {courses.map((c) => (
            <div
              key={c.title}
              className="rounded-[14px] p-3 flex items-center gap-3"
              style={{ background: "#ffffff", border: "0.5px solid #e8e0d8" }}
              onClick={onTap}
            >
              <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FDECEA" }}>
                {badgeType === "not_started" ? (
                  <Clock className="h-4 w-4" style={{ color: "#F44321" }} />
                ) : (
                  <AlertCircle className="h-4 w-4" style={{ color: "#B07010" }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium truncate" style={{ color: "#1B2D5B" }}>{c.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 rounded-full" style={{ background: "#f0e8e0" }}>
                    <div className="h-full rounded-full" style={{ width: `${c.rate}%`, background: "#F44321" }} />
                  </div>
                  <span className="text-[11px] font-semibold shrink-0" style={{ color: "#1B2D5B" }}>{c.rate}%</span>
                </div>
              </div>
              <StatusBadge type={badgeType} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ type }: { type: "not_started" | "in_progress" }) {
  const styles =
    type === "not_started"
      ? { background: "#FEF0ED", color: "#C03010" }
      : { background: "#FFF7E6", color: "#B07010" };
  const text = type === "not_started" ? "Not started" : "In progress";
  return (
    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap shrink-0" style={styles}>
      {text}
    </span>
  );
}